import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useLocalization } from "@fluent/react";

import { InstitutionLogo } from "cerulean";

type ExternalLinkProps = {
  children?: ReactNode;
  href?: string;
  title?: string;
};

type SocialMediaIconProps = {
  title?: string;
  href?: string;
  iconClassName?: string;
};

type RateDefinitionsProps = {
  isMobile?: boolean;
};

type AzulFooterProps = {
  institutionSettings: API.Institution;
  ehlLogoUrl?: string;
};

const useViewport = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleWindowResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  return width;
};

const ExternalLink = ({ children, href, title = "" }: ExternalLinkProps) => (
  <a
    className="external-link"
    target="_blank"
    rel="noopener noreferrer"
    title={title}
    href={href}
  >
    {children}
  </a>
);

const SocialMediaIcon = ({
  title,
  href,
  iconClassName,
}: SocialMediaIconProps) => (
  <span className="social-media-icon">
    <ExternalLink title={title} href={href}>
      <i className={`socialIcon ${iconClassName}`}></i>
    </ExternalLink>
  </span>
);

const AzulFooter = ({ ehlLogoUrl, institutionSettings }: AzulFooterProps) => {
  const { l10n } = useLocalization();
  const width = useViewport();
  const mobileBreakpoint = 768; // 768 is the standard max size of mobile (see $tablet)

  const Copyright = () => {
    const memberDif = institutionSettings.member_dif;
    const difLogoUrl = institutionSettings.dif_logo_url;
    const fdicNcuaLogoUrl = institutionSettings.fdic_ncua_logo_url;
    const customFooterSetting = institutionSettings.custom_footer;
    const institutionType = institutionSettings.type;
    const equalHousingLogo =
      institutionSettings.equal_housing_logo_url || ehlLogoUrl;

    let customFooter = `${new Date().getFullYear()} ${
      institutionSettings.long_name
    }`;
    if (customFooterSetting) {
      customFooter = `${new Date().getFullYear()} ${
        institutionSettings.long_name
      }, ${customFooterSetting}`;
    }

    const getMembershipText = () => {
      const isMemberDif = memberDif;
      const isCreditUnion = institutionType === "credit_union";
      const hasDifLogo = difLogoUrl;
      if (isCreditUnion) {
        return l10n.getString("membership-ncua");
      }
      if (!isCreditUnion && isMemberDif && !hasDifLogo) {
        return l10n.getString("membership-fdic-dif");
      }
      return l10n.getString("membership-fdic");
    };

    return (
      <div className="footer-copyright">
        <div>
          <p className="nds-p3">&copy; {customFooter}</p>
          <p className="nds-p3">{getMembershipText()}</p>
          <p className="nds-p3">{l10n.getString("powered-by-narmi")}</p>
        </div>
        <div className="copyright-image">
          {equalHousingLogo && (
            <img
              src={equalHousingLogo}
              alt={l10n.getString("equal-housing-alt-text")}
              className="footer-ehl-logo"
            />
          )}
          {memberDif && difLogoUrl && (
            <img
              alt={l10n.getString("member-bank-dif-alt-text")}
              src={difLogoUrl}
              style={{ marginLeft: "3px", width: "30px" }}
            />
          )}
          {fdicNcuaLogoUrl && (
            <img
              alt={l10n.getString("member-bank-fdic-ncua-alt-text")}
              src={fdicNcuaLogoUrl}
              style={{ marginLeft: "3px", width: "48px" }}
            />
          )}
        </div>
      </div>
    );
  };

  const RateDefinitions = ({ isMobile = false }: RateDefinitionsProps) => (
    <div className="footer-rate-definitions">
      <p className="nds-p3 medium-grey">
        {isMobile ? (
          <>
            {l10n.getString("apr-label-footer")}
            <br />
            {l10n.getString("apy-label-footer")}
          </>
        ) : (
          <>{`${l10n.getString("apy-label-footer")} ${l10n.getString(
            "apr-label-footer"
          )}`}</>
        )}
      </p>
    </div>
  );

  const BankInfo = () => {
    const routingNumber = institutionSettings.routing_number;
    const locationHoursUrl = institutionSettings.location_hours_url;
    const termsUrl = institutionSettings.terms_url;
    const privacyUrl = institutionSettings.privacy_url;
    const showSupportLink = institutionSettings.show_support_footer_link;

    return (
      <>
        <p className="nds-subheader">{l10n.getString("heading-fi-info")}</p>
        <p className="nds-p3">
          {l10n.getString("footer-info-routing-number", {
            routingNumber: routingNumber || "",
          })}
        </p>
        {locationHoursUrl && (
          <p className="nds-p3">
            <ExternalLink href={locationHoursUrl}>
              {l10n.getString("footer-view-locations-and-hours")}
            </ExternalLink>
          </p>
        )}
        {!!termsUrl && (
          <p className="nds-p3">
            <ExternalLink href={termsUrl}>
              {l10n.getString("footer-terms")}
            </ExternalLink>
          </p>
        )}
        {!!privacyUrl && (
          <p className="nds-p3">
            <ExternalLink href={privacyUrl}>
              {l10n.getString("disclosure-privacy")}
            </ExternalLink>
          </p>
        )}
        {showSupportLink && (
          <p className="nds-p3">
            <a className="external-link" href="/support">
              {l10n.getString("footer-support")}
            </a>
          </p>
        )}
      </>
    );
  };

  const ContactUs = () => {
    const helpPhone = institutionSettings.help_phone;
    const cardPhone = institutionSettings.card_phone;
    const lostCardUrl = institutionSettings.lost_card_url;
    const helpEmail = institutionSettings.help_email;
    const chatScript = institutionSettings.chat_script;
    const footerDisplaySelectors = institutionSettings.digital_banking_footer_display_selectors;
    const showHelpPhone = footerDisplaySelectors?.show_help_phone ?? true;
    const showLostCard = footerDisplaySelectors?.show_lost_card ?? true;
    const showHelpEmail = footerDisplaySelectors?.show_help_email ?? true;

    // Do not render if there's no info to display in Contact column (unlikely scenario)
    if (
      !(showHelpPhone && helpPhone) &&
      !(showLostCard && (cardPhone || lostCardUrl)) &&
      !chatScript &&
      !(showHelpEmail && helpEmail)
    )
      return null;

    return (
      <>
        <p className="nds-subheader">{l10n.getString("footer-contact-us")}</p>

        {showHelpPhone && helpPhone && (
          <p className="nds-p3">
            <a className="grey-link" href={`tel:${helpPhone}`}>
              {l10n.getString("support-link-phone", { helpPhone })}
            </a>
          </p>
        )}

        {showLostCard &&
          ((cardPhone && (
            <p className="nds-p3">
              <a className="grey-link" href={`tel:${cardPhone}`}>
                {l10n.getString("support-link-lost-card-phone", {
                  cardPhone,
                })}
              </a>
            </p>
          )) ||
            (lostCardUrl && (
              <p className="nds-p3">
                <a className="grey-link" href={lostCardUrl}>
                  {l10n.getString("support-link-lost-card")}
                </a>
              </p>
            )))}
        {(chatScript && (
          <p className="nds-p3">
            {/* Existing backend-provided setting executes embedded live-chat JS. */}
            {/* eslint-disable-next-line no-eval */}
            <a onClick={() => eval(chatScript)}>
              {l10n.getString("support-link-cs-chat")}
            </a>
          </p>
        )) ||
          (showHelpEmail && helpEmail && (
            <p className="nds-p3">
              <a className="grey-link" href={`mailto:${helpEmail}`}>
                {l10n.getString("support-link-email-us", {
                  helpEmail,
                })}
              </a>
            </p>
          ))}
      </>
    );
  };

  const SocialMediaIcons = () => {
    const { facebook, twitter, instagram, linkedin } = institutionSettings;
    return (
      <div className="social-media-icons">
        {facebook && (
          <SocialMediaIcon
            title={l10n.getString("social-facebook")}
            href={facebook}
            iconClassName="narmi-icon-facebook"
          />
        )}
        {twitter && (
          <SocialMediaIcon
            title={l10n.getString("social-twitter")}
            href={twitter}
            iconClassName="narmi-icon-twitter"
          />
        )}
        {instagram && (
          <SocialMediaIcon
            title={l10n.getString("social-instagram")}
            href={instagram}
            iconClassName="narmi-icon-instagram"
          />
        )}
        {linkedin && (
          <SocialMediaIcon
            title={l10n.getString("social-linkedin")}
            href={linkedin}
            iconClassName="narmi-icon-linkedin"
          />
        )}
      </div>
    );
  };

  const MobileFooter = () => (
    <div className="nds-span-6">
      <InstitutionLogo
        className="footer-logo"
        institution={{
          logo: institutionSettings.logo_url,
          name: institutionSettings.long_name,
        }}
      />
      <div className="fi-information nds-span-6">
        <div className="fi-information-column">
          <BankInfo />
        </div>
        <div className="fi-information-column">
          <ContactUs />
          <SocialMediaIcons />
        </div>
      </div>
      <div>
        <Copyright />
        <RateDefinitions isMobile />
      </div>
    </div>
  );

  const DesktopFooter = () => (
    <>
      <div className="nds-span-6">
        <InstitutionLogo
          className="footer-logo"
          institution={{
            logo: institutionSettings.logo_url,
            name: institutionSettings.long_name,
          }}
        />
        <Copyright />
        <RateDefinitions />
      </div>
      <div className="fi-information nds-span-6">
        <div className="fi-information-column">
          <BankInfo />
        </div>
        <div className="fi-information-column">
          <ContactUs />
          <SocialMediaIcons />
        </div>
      </div>
    </>
  );
  // The footer design has different order for narrow vs. wide screens
  return (
    <div className="narmi-grid-container grid-footer" style={{ flexGrow: 1 }}>
      <div className="footer-container background-db narmi-grid-content">
        <div className="nds-grid">
          {width < mobileBreakpoint ? <MobileFooter /> : <DesktopFooter />}
        </div>
      </div>
    </div>
  );
};

export default AzulFooter;
